/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { getPagePath, trans } from '@spotahome/soyuz/client';

import { DSIcon } from '@spotahome/ui-library/base';
import LandingSection from '@spotahome/ui-library/src/tenant/components/LandingSection/LandingSection';
import useHorizontalDrag from '@spotahome/ui-library/src/hooks/useHorizontalDrag';
import BaseChip from '@spotahome/ui-library/src/tenant/components/BaseChip/BaseChip';
import CarouselDots from '@spotahome/ui-library/src/tenant/components/CarouselDots';

import './TopCities.scss';

const TopCity = ({ cityId, cityName, countryId, totalCount, onClick }) => (
  <li>
    <a
      className="top-cities__city"
      href={getPagePath('marketplace.free-search', {
        params: { locationString: cityId }
      })}
      onClick={onClick}
      draggable="false"
    >
      <BaseChip
        label={`+${totalCount} ${trans('historical.properties')}`}
        colorSchema={BaseChip.SCHEMAS.light}
        className="top-cities__city-count"
      />
      <img
        draggable="false"
        src={`https://static.spotahome.com/seo-country-pages/${countryId}/${cityId}.webp`}
        alt={cityName}
      />
      <div className="top-cities__city-name-container">
        <h3 className="top-cities__city-name">{cityName}</h3>
        <DSIcon name="arrow_right" className="top-cities__city-icon" />
      </div>
    </a>
  </li>
);

const TOTAL_COUNT_KEYS = ['apartments', 'residences', 'studios', 'rooms'];

const getTotalCount = city => {
  const totalCount = TOTAL_COUNT_KEYS.reduce((acc, key) => acc + city[key], 0);

  return Math.round(totalCount / 100) * 100;
};

const TopCities = ({ countryId, countryName, cities }) => {
  const [selectedItem, setSelectedItem] = useState(0);
  const handleSelectedItem = index => {
    setSelectedItem(index);
  };
  const {
    containerRef,
    isDragging,
    handleMouseDown,
    handleMouseUp,
    handleTouchStart,
    handleTouchEnd,
    navigateToItem
  } = useHorizontalDrag({
    onMoveStart: () => {},
    onMove: handleSelectedItem,
    itemsOnLeftClassName: 'section-content-navigation__list--items-on-left',
    itemsOnRightClassName: 'section-content-navigation__list--items-on-right'
  });

  const handleItemClick = cityId => e => {
    e.preventDefault();
    e.stopPropagation();
    if (isDragging()) {
      return;
    }

    const path = getPagePath('marketplace.free-search', {
      params: { locationString: cityId }
    });

    window.location.href = path;
  };

  useEffect(() => {
    navigateToItem(selectedItem);
  }, [selectedItem]);

  const topCities = cities.filter(city => city.top);

  return (
    <>
      <LandingSection
        className="top-cities"
        containerClassName="top-cities__container"
        observableSection="top-cities"
      >
        <h2 className="top-cities__title">
          {trans('countries.top-cities.title', { country: countryName })}
        </h2>
      </LandingSection>
      <LandingSection
        className="top-cities"
        containerClassName="top-cities__container--list"
      >
        <ul
          ref={containerRef}
          className="top-cities__list"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {topCities.map(city => (
            <TopCity
              key={city.id}
              cityId={city.id}
              cityName={city.name}
              countryId={countryId}
              totalCount={getTotalCount(city)}
              onClick={handleItemClick(city.id)}
            />
          ))}
        </ul>
        <div className="top-cities__dots">
          <CarouselDots
            nItems={topCities.length}
            onDotClick={handleSelectedItem}
            selectedItem={selectedItem}
            theme="dark"
            differentSize={false}
            dotSize="large"
          />
        </div>
      </LandingSection>
    </>
  );
};

TopCities.propTypes = {
  countryId: PropTypes.string.isRequired,
  countryName: PropTypes.string.isRequired,
  cities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      apartments: PropTypes.number.isRequired,
      residences: PropTypes.number.isRequired,
      studios: PropTypes.number.isRequired,
      rooms: PropTypes.number.isRequired,
      top: PropTypes.bool.isRequired
    })
  ).isRequired
};

export default TopCities;
